/**
 * Historic Service
 *
 * Manages toast history in localStorage with modern features:
 * - Persistent storage across sessions
 * - Automatic cleanup of old entries
 * - URL tracking for navigation
 * - Maximum capacity management
 */

const STORAGE_KEY = "itm_toast_history";
const MAX_ENTRIES = 50;
const RETENTION_DAYS = 7;

/**
 * Generate a unique ID for entries
 */
const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get all history entries from localStorage
 */
export const getHistory = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const entries = JSON.parse(stored);
    return Array.isArray(entries) ? entries : [];
  } catch (error) {
    console.error("Error reading toast history:", error);
    return [];
  }
};

/**
 * Save history to localStorage
 */
const saveHistory = (entries) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (error) {
    console.error("Error saving toast history:", error);
  }
};

/**
 * Add a toast entry to history
 * @param {Object} entry - The entry to add
 * @param {string} entry.message - The toast message
 * @param {string} entry.type - 'success' | 'error' | 'warning' | 'info'
 * @param {string} entry.url - The URL where the toast was triggered
 */
export const addHistoryEntry = ({ message, type, url }) => {
  if (!message) return null;

  const entry = {
    id: generateId(),
    message,
    type: type || "info",
    url: url || window.location.pathname,
    timestamp: new Date().toISOString(),
  };

  const history = getHistory();

  // Add new entry at the beginning
  history.unshift(entry);

  // Enforce maximum capacity
  const trimmedHistory = history.slice(0, MAX_ENTRIES);

  // Remove old entries beyond retention period
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - RETENTION_DAYS);
  const cleanedHistory = trimmedHistory.filter(
    (e) => new Date(e.timestamp) > cutoffDate
  );

  saveHistory(cleanedHistory);

  return entry;
};

/**
 * Delete a specific entry
 * @param {string} id - The entry ID
 */
export const deleteEntry = (id) => {
  const history = getHistory();
  const updated = history.filter((e) => e.id !== id);
  saveHistory(updated);
};

/**
 * Clear all history
 */
export const clearHistory = () => {
  saveHistory([]);
};

/**
 * Format a timestamp for display
 * @param {string} isoTimestamp - ISO timestamp string
 * @returns {Object} Formatted time strings
 */
export const formatTimestamp = (isoTimestamp) => {
  const date = new Date(isoTimestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  let relative;
  if (diffMins < 1) {
    relative = "À l'instant";
  } else if (diffMins < 60) {
    relative = `Il y a ${diffMins} min`;
  } else if (diffHours < 24) {
    relative = `Il y a ${diffHours}h`;
  } else if (diffDays === 1) {
    relative = "Hier";
  } else if (diffDays < 7) {
    relative = `Il y a ${diffDays} jours`;
  } else {
    relative = date.toLocaleDateString("fr-FR");
  }

  const full = date.toLocaleString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  return { relative, full };
};

export default {
  getHistory,
  addHistoryEntry,
  deleteEntry,
  clearHistory,
  formatTimestamp,
};
